/**
 * Retry logic and exponential backoff utilities for the BD SDK.
 *
 * @remarks
 * Provides configurable retry mechanisms with exponential backoff and jitter
 * for handling transient failures in API calls and network operations.
 *
 * @module Retry Logic
 * @category Internal
 * @packageDocumentation
 */
// Classifiers are inlined into the helper methods below to avoid extra types
/**
 * Shared RetryService encapsulates retry behavior with injected logger and wait parameters.
 */
/**
 * Internal helper that provides HTTP and RPC retry with exponential backoff and jitter.
 *
 * @remarks
 * - Intended for internal module usage (Common, Chains, DeFi).
 * - Does NOT change the type of the wrapped operation; the generic return type `T` is preserved.
 * - Uses `logger.warn` for retry scheduling and `logger.error` on failures before a retry.
 *
 * Example:
 * ```ts
 * const retry = new RetryService({ logger, wait: { maxAttempts: 2, baseDelayMs: 200, jitterMs: 200 } });
 * const result = await retry.http(() => httpClient.get(...));
 * ```
 *
 * @internal
 */
export class RetryService {
    logger;
    wait;
    labelPrefix;
    computeBackoff(attempt) {
        return (this.wait.baseDelayMs * 2 ** (attempt - 1) + Math.floor(Math.random() * this.wait.jitterMs));
    }
    /**
     * Creates a new RetryService.
     * @param args.logger Optional logger for retry diagnostics
     * @param args.wait Backoff and attempts configuration
     * @param args.labelPrefix Optional label prefix for log messages (informational)
     */
    constructor(args) {
        this.logger = args.logger;
        this.wait = args.wait;
        this.labelPrefix = args.labelPrefix || "Retry";
    }
    /**
     * Executes an RPC operation with retry.
     *
     * @typeParam T - The resolved return type of the operation
     * @param op The RPC operation to execute
     * @returns The same type returned by the operation
     */
    async rpc(op, label) {
        const tag = label ? `[${label}] ` : "";
        const wait = this.wait ?? {
            maxAttempts: 2,
            baseDelayMs: 200,
            jitterMs: 200,
        };
        let lastError;
        for (let attempt = 1; attempt <= wait.maxAttempts; attempt++) {
            try {
                return await op();
            }
            catch (err) {
                lastError = err;
                this.logger?.error(`${tag}RPC operation failed on attempt ${attempt}`, err);
                if (attempt >= wait.maxAttempts)
                    break;
                const message = err instanceof Error ? err.message : String(err);
                const retry = message.includes("429") ||
                    message.includes("Too Many Requests") ||
                    message.includes("cannot start up") ||
                    message.includes("detect network");
                if (!retry)
                    break;
                const waitMs = this.computeBackoff(attempt);
                this.logger?.warn(`${tag}RPC retry scheduled after ${waitMs}ms (attempt ${attempt})`, err);
                await new Promise((r) => setTimeout(r, waitMs));
            }
        }
        throw lastError instanceof Error ? lastError : new Error(String(lastError));
    }
}
//# sourceMappingURL=data:application/json;base64,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